// Мир кампании: концепции, голосование, ресурсы/институты/фракции.
// Вынесено из api.ts (Фаза 1, разбор клиента по доменам).

import { request, LONG_ACTION_TIMEOUT_MS } from "./client";
import { type MediaAsset } from "./media";

export type SetupPhase =
  | "COLLECTING_WORLD_PROPOSALS"
  | "WORLD_VOTING"
  | "WORLD_GENERATING"
  | "RANDOM_ROLE_ASSIGNMENT"
  | "PRIVATE_BRIEFING"
  | "READY";
export type ProposalModerationStatus = "APPROVED" | "NEEDS_REVISION";
export type ProposalModerationSource =
  "LEGACY" | "LOCAL" | "AI" | "LOCAL_FALLBACK";
export interface WorldResource {
  code: string;
  displayName: string;
  description: string;
  currentValue: number;
  maxValue: number;
}
export interface WorldIndicator {
  code: string;
  displayName: string;
  description: string;
  currentValue: number;
}
export interface WorldInstitution {
  code: string;
  displayName: string;
  description: string;
  level: number;
  load: number;
}
export interface WorldFaction {
  code: string;
  displayName: string;
  description: string;
  influence: number;
  attitude: number;
  demands: string;
  support: string;
}
export interface WorldVisualCanon {
  version: number;
  era: string;
  technology: string;
  architecture: string[];
  clothing: string[];
  weaponsAndTools: string[];
  transport: string[];
  materials: string[];
  environment: string[];
  palette: string[];
  artDirection: string;
  typicalPeople: string[];
  identityAnchors: string[];
  forbiddenElements: string[];
  hybridRules: string[];
}
export interface WorldContextTheme {
  code: string;
  title: string;
  description: string;
}
export interface World {
  id: string;
  worldName: string;
  concept: string;
  settingType: string;
  technologyLevel: string;
  governanceName: string;
  visualStyle: string;
  visualCanonVersion: number;
  visualCanon: WorldVisualCanon;
  summary: string;
  openingNarrative: string;
  openingImagePrompt: string;
  generationProvider: string;
  openingImages: MediaAsset[];
  contextThemes: WorldContextTheme[];
  resources: WorldResource[];
  indicators: WorldIndicator[];
  institutions: WorldInstitution[];
  factions: WorldFaction[];
  createdAt: string;
}
export interface WorldProposal {
  id: string;
  authorUserId: string;
  authorDisplayName: string;
  worldName: string;
  concept: string;
  voteCount: number;
  createdAt: string;
  moderationStatus: ProposalModerationStatus;
  moderationScore: number;
  moderationIssues: string[];
  moderationSuggestions: string[];
  moderationSource: ProposalModerationSource;
  revisionNumber: number;
  isMine: boolean;
  isEligibleForVoting: boolean;
}
export interface WorldSelection {
  phase: SetupPhase;
  proposals: WorldProposal[];
  myProposalId?: string;
  myVoteProposalId?: string;
  skippedUserIds: string[];
  readyUserIds: string[];
  myProposalSkipped: boolean;
  canSubmitProposal: boolean;
  canSkipProposal: boolean;
  canOpenVoting: boolean;
  canVote: boolean;
  canFinalizeVoting: boolean;
  canForceFinalizeVoting: boolean;
  generationFailed: boolean;
  generationFailureCode?: string;
  generationFailureMessage?: string;
  generationFailedAt?: string;
  selectedProposalId?: string;
  approvedProposalCount: number;
  needsRevisionCount: number;
}

export const worldApi = {
  submitWorldProposal: (
    campaignId: string,
    worldName: string,
    concept: string,
  ) =>
    request<WorldProposal>(
      `/api/campaigns/${campaignId}/setup/world-proposals`,
      { method: "POST", body: JSON.stringify({ worldName, concept }) },
      LONG_ACTION_TIMEOUT_MS,
    ),
  skipWorldProposal: (campaignId: string) =>
    request<WorldSelection>(
      `/api/campaigns/${campaignId}/setup/world-proposals/skip`,
      { method: "POST", body: JSON.stringify({}) },
      LONG_ACTION_TIMEOUT_MS,
    ),
  openWorldVoting: (campaignId: string) =>
    request<WorldSelection>(
      `/api/campaigns/${campaignId}/setup/world-voting/open`,
      { method: "POST" },
      LONG_ACTION_TIMEOUT_MS,
    ),
  castWorldVote: (campaignId: string, proposalId: string) =>
    request<WorldSelection>(
      `/api/campaigns/${campaignId}/setup/world-voting/vote`,
      { method: "POST", body: JSON.stringify({ proposalId }) },
    ),
  finalizeWorldVoting: (campaignId: string) =>
    request<WorldSelection>(
      `/api/campaigns/${campaignId}/setup/world-voting/finalize`,
      { method: "POST" },
      LONG_ACTION_TIMEOUT_MS,
    ),
  forceFinalizeWorldVoting: (campaignId: string) =>
    request<WorldSelection>(
      `/api/campaigns/${campaignId}/setup/world-voting/finalize-force`,
      { method: "POST" },
      LONG_ACTION_TIMEOUT_MS,
    ),
};
